import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <main className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading text-foreground-900 mb-4">文章未找到</h1>
          <Link
            to="/"
            className="px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </main>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.category === article.category)
    .slice(0, 2);

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.summary}
        keywords={`黄游网页版,${article.tags.join(',')},${article.title}`}
        canonicalUrl={`https://huangyou.com/article/${article.id}`}
        ogType="article"
      />
      <SchemaOrg
        type="Article"
        data={{
          headline: article.title,
          description: article.summary,
          author: article.author,
          datePublished: article.date,
          dateModified: article.date,
        }}
      />
      <article className="min-h-screen bg-background-50">
        <div className="max-w-[900px] mx-auto px-4 md:px-6 pt-24 pb-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: '/article/article-001' },
              { label: article.title },
            ]}
          />

          <header className="mt-6 mb-8">
            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-md mb-4">
              {article.category}
            </span>
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-400">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.readCount} 阅读
              </span>
            </div>
          </header>

          <div className="relative w-full h-64 md:h-80 rounded-lg overflow-hidden mb-10">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-base md:prose-lg max-w-none text-foreground-700 leading-relaxed
              [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-heading [&_h2]:text-foreground-900 [&_h2]:mt-8 [&_h2]:mb-4
              [&_p]:mb-4 [&_p]:leading-relaxed
              [&_strong]:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 flex flex-wrap items-center gap-2">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>

          {relatedArticles.length > 0 && (
            <section className="mt-16 pt-10 border-t border-background-200/70">
              <h3 className="text-xl font-heading text-foreground-900 mb-6">相关文章</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group flex gap-4 p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-200 transition-all duration-300 cursor-pointer no-underline"
                  >
                    <div className="w-24 h-24 md:w-28 md:h-28 flex-shrink-0 rounded-md overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-heading text-foreground-900 mb-1.5 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-500 line-clamp-2 mb-2">{ra.summary}</p>
                      <span className="text-xs text-foreground-400">{ra.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>
      </article>
    </>
  );
}